"use client";

/* ═══════════════════════════════════════════════════════════════════════════
   EXTRA ADD-ONS  ·  The Hexaplay Super Admin inventory module
   ─────────────────────────────────────────────────────────────────────────
   • WORKS IN: CRA (jsx) / Vite (jsx) / Next.js (jsx) — no TypeScript.
   • REAL DATA ONLY: there is ZERO sample / dummy / seed data in this file.
     If the server has no add-ons the screen shows an empty state and asks
     you to create the first real item.
   • CROSS-BROWSER (Chrome, Edge, Brave, Firefox, incognito, other devices):
     the DATABASE is the single source of truth. localStorage is only used as
     an offline *cache of data that came from the server*, never as a source
     of new items. Two different browsers therefore always show the same list,
     the same prices and the same Current Stock.
   • FROZEN-ORDER DEDUCTIONS are written to the server (POST /deduct-order),
     so a booking confirmed in browser A instantly reduces Current Stock in
     browser B, C, D … on the next poll / broadcast / focus event.
   ═══════════════════════════════════════════════════════════════════════════ */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./extraaddon.css";

/* ═══════════════════════════════════════════════════════════════════════════
   1. API CONFIG — auto-detects CRA / Vite / Next.js environments
═══════════════════════════════════════════════════════════════════════════ */
const RAW_API_BASE =
  (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) ||
  (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_API_URL) ||
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) ||
  "";

const CLEAN_API_BASE = String(RAW_API_BASE || "").trim().replace(/\/+$/, "");
const API_URL = CLEAN_API_BASE
  ? CLEAN_API_BASE.endsWith("/api")
    ? `${CLEAN_API_BASE}/extra-addons`
    : `${CLEAN_API_BASE}/api/extra-addons`
  : "/api/extra-addons";

/* ═══════════════════════════════════════════════════════════════════════════
   2. CONSTANTS
═══════════════════════════════════════════════════════════════════════════ */
const CATEGORIES = [
  { key: "all", label: "All Items", emoji: "📦", color: "#6366f1" },
  { key: "cakes", label: "Cakes", emoji: "🎂", color: "#ec4899" },
  { key: "decorations", label: "Decorations", emoji: "🎨", color: "#f59e0b" },
  { key: "gifts", label: "Gifts", emoji: "🎁", color: "#06b6d4" },
  { key: "services", label: "Services", emoji: "🛎️", color: "#8b5cf6" },
];

const VALID_CATEGORIES = ["cakes", "decorations", "gifts", "services"];

const EMOJI_PRESETS = [
  "🎁", "🎂", "🕯️", "🎈", "🎊", "🎉", "✨", "🌹", "💐", "🍫",
  "🥂", "👑", "💍", "📸", "🎨", "🛎️", "🎀", "⭐", "🪅", "📦",
];

const SORTS = [
  { key: "newest", label: "Newest first" },
  { key: "oldest", label: "Oldest first" },
  { key: "name-asc", label: "Name: A → Z" },
  { key: "name-desc", label: "Name: Z → A" },
  { key: "price-desc", label: "Price: High → Low" },
  { key: "price-asc", label: "Price: Low → High" },
  { key: "stock-asc", label: "Stock: Low → High" },
  { key: "stock-desc", label: "Stock: High → Low" },
];

/* Cache of *server* data only — lets the page paint instantly and survive a
   dropped connection. It is never used to invent items. */
const CACHE_KEY = "hexaplay:extra-addons:cache:v2";
const SYNC_CHANNEL = "hexaplay:extra-addons";
const POLL_MS = 8000;

/* ═══════════════════════════════════════════════════════════════════════════
   3. HELPERS
═══════════════════════════════════════════════════════════════════════════ */
const isBrowser = () => typeof window !== "undefined";

const n = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => `₹${n(value).toLocaleString("en-IN")}`;

const normName = (name) =>
  String(name || "").trim().toLowerCase().replace(/\s+/g, " ");

const getCategoryInfo = (key) =>
  CATEGORIES.find((c) => c.key === key) || CATEGORIES[0];

/** Server row → UI row. Accepts Mongo `_id` or SQL `id`. */
const normalizeItem = (item) => {
  if (!item) return null;
  const idStr = String(item.id || item._id || "").trim();
  if (!idStr) return null;
  const category = VALID_CATEGORIES.includes(String(item.category || "").toLowerCase())
    ? String(item.category).toLowerCase()
    : "gifts";
  return {
    ...item,
    id: idStr,
    _id: idStr,
    name: String(item.name || "Untitled add-on").trim(),
    sku: item.sku ? String(item.sku).trim().toUpperCase() : "",
    category,
    emoji: item.emoji || getCategoryInfo(category).emoji,
    description: item.description ? String(item.description) : "",
    price: Math.max(0, Math.round(n(item.price))),
    stock: Math.max(0, Math.floor(n(item.stock))),
    minStock: Math.max(1, Math.floor(n(item.minStock || 5))),
    isActive: item.isActive !== false && item.is_active !== false,
    createdAt: item.createdAt || item.created_at || "",
    updatedAt: item.updatedAt || item.updated_at || "",
  };
};

const readCache = () => {
  if (!isBrowser()) return { items: [], savedAt: 0 };
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return { items: [], savedAt: 0 };
    const parsed = JSON.parse(raw);
    const rows = Array.isArray(parsed?.items) ? parsed.items.map(normalizeItem).filter(Boolean) : [];
    return { items: rows, savedAt: n(parsed?.savedAt) };
  } catch (e) {
    return { items: [], savedAt: 0 };
  }
};

const writeCache = (items) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ items, savedAt: Date.now() })
    );
  } catch (e) {
    /* quota / private mode — non fatal */
  }
};

const formatTime = (ts) => {
  const d = new Date(ts);
  if (!ts || Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const relativeTime = (ts) => {
  const d = new Date(ts).getTime();
  if (!ts || Number.isNaN(d)) return "";
  const diff = Math.max(0, Date.now() - d);
  if (diff < 45000) return "just now";
  if (diff < 3600000) return `${Math.round(diff / 60000)} min ago`;
  if (diff < 86400000) return `${Math.round(diff / 3600000)} h ago`;
  return `${Math.round(diff / 86400000)} d ago`;
};

/** Deduction request → normalised list the server understands. */
const buildDeductPayload = (rawItems) =>
  (Array.isArray(rawItems) ? rawItems : [])
    .map((d) => ({
      name: d?.name ? String(d.name) : "",
      sku: d?.sku ? String(d.sku) : "",
      qty: Math.max(0, Math.floor(n(d?.qty ?? d?.quantity ?? 1))),
      // Absolute stock (if the order screen frozen the value) wins over qty.
      newStock:
        d?.newStock !== undefined && d?.newStock !== null && d?.newStock !== ""
          ? Math.max(0, Math.floor(n(d.newStock)))
          : undefined,
    }))
    .filter((d) => d.name || d.sku);

/* ═══════════════════════════════════════════════════════════════════════════
   4. SMALL UI PIECES
═══════════════════════════════════════════════════════════════════════════ */
const Toast = ({ toast, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4200);
    return () => clearTimeout(timer);
  }, [onClose]);

  const icon =
    toast.type === "success" ? "✅"
      : toast.type === "error" ? "❌"
      : toast.type === "warning" ? "⚠️"
      : "ℹ️";

  return (
    <div className={`ea-toast ea-toast--${toast.type}`} role="status">
      <span className="ea-toast__icon">{icon}</span>
      <span className="ea-toast__message">{toast.message}</span>
      <button className="ea-toast__close" onClick={onClose} aria-label="Dismiss">
        ✕
      </button>
    </div>
  );
};

const AlertBanner = ({ items, onFilter }) => {
  const out = items.filter((i) => i.stock === 0);
  const low = items.filter((i) => i.stock > 0 && i.stock <= i.minStock);
  if (!out.length && !low.length) return null;

  return (
    <div className="ea-alerts">
      {out.length > 0 && (
        <button type="button" className="ea-alert ea-alert--out" onClick={() => onFilter("out")}>
          <span className="ea-alert__icon">🚫</span>
          <span className="ea-alert__body">
            <span className="ea-alert__title">
              Out of stock — {out.length} item{out.length > 1 ? "s" : ""}
            </span>
            <span className="ea-alert__chips">
              {out.slice(0, 8).map((i) => (
                <span key={i.id} className="ea-alert__chip ea-alert__chip--out">
                  {i.emoji} {i.name}
                </span>
              ))}
              {out.length > 8 && <span className="ea-alert__more">+{out.length - 8} more</span>}
            </span>
          </span>
        </button>
      )}
      {low.length > 0 && (
        <button type="button" className="ea-alert ea-alert--low" onClick={() => onFilter("low")}>
          <span className="ea-alert__icon">⚠️</span>
          <span className="ea-alert__body">
            <span className="ea-alert__title">
              Low stock — {low.length} item{low.length > 1 ? "s" : ""} below minimum
            </span>
            <span className="ea-alert__chips">
              {low.slice(0, 8).map((i) => (
                <span key={i.id} className="ea-alert__chip ea-alert__chip--low">
                  {i.emoji} {i.name} ({i.stock} left)
                </span>
              ))}
              {low.length > 8 && <span className="ea-alert__more">+{low.length - 8} more</span>}
            </span>
          </span>
        </button>
      )}
    </div>
  );
};

const MOVEMENT_META = {
  restock: { icon: "📥", label: "Restock", cls: "in" },
  deduct: { icon: "📤", label: "Deduction", cls: "out" },
  adjust: { icon: "🔧", label: "Adjustment", cls: "adj" },
  order: { icon: "🔒", label: "Order confirmed", cls: "out" },
};

const ActivityLog = ({ orderEvents, movements, onRefresh }) => {
  const [open, setOpen] = useState(false);
  if (!orderEvents.length && !movements.length) return null;

  return (
    <section className="ea-activity">
      <button type="button" className="ea-activity__toggle" onClick={() => setOpen((v) => !v)}>
        <span>📝 Stock activity &amp; frozen-order reductions</span>
        <span className="ea-activity__count">{orderEvents.length + movements.length}</span>
        <span className={`ea-activity__chev ${open ? "open" : ""}`}>⌄</span>
      </button>

      {open && (
        <div className="ea-activity__panel">
          <div className="ea-activity__list">
            {orderEvents.map((ev) => (
              <div key={ev.id} className="ea-activity__item">
                <div className="ea-activity__icon">🔒</div>
                <div className="ea-activity__details">
                  <div className="ea-activity__title">
                    <strong>Order confirmed:</strong> {ev.orderName || "Customer"}
                    {ev.theater ? <span> · {ev.theater}</span> : null}
                    {ev.eventDate ? <span> · {ev.eventDate}</span> : null}
                    {ev.deduped ? <span> · already applied</span> : null}
                  </div>
                  <div className="ea-activity__time">🕐 {formatTime(ev.timestamp || ev.createdAt)}</div>
                  <div className="ea-activity__chips">
                    {(ev.items || []).map((it, i) => (
                      <span key={i} className="ea-chip ea-chip--out">
                        {it.emoji || "🎁"} {it.name}: −{it.qty ?? it.quantity} → {it.newStock ?? "—"}
                      </span>
                    ))}
                    {(ev.unmatched || []).map((name, i) => (
                      <span key={`u-${i}`} className="ea-chip ea-chip--warn">
                        ⚠️ {name}: not in inventory
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {movements.map((m) => {
              const meta = MOVEMENT_META[m.type] || MOVEMENT_META.adjust;
              return (
                <div key={m.id} className="ea-activity__item">
                  <div className={`ea-activity__icon ea-activity__icon--${meta.cls}`}>{meta.icon}</div>
                  <div className="ea-activity__details">
                    <div className="ea-activity__title">
                      <strong>{meta.label}:</strong> {m.addonName || m.addon_name}
                      {m.orderName || m.order_name ? (
                        <span> · {m.orderName || m.order_name}</span>
                      ) : null}
                    </div>
                    <div className="ea-activity__time">
                      🕐 {formatTime(m.createdAt || m.created_at)}
                      {m.note ? <span> · {m.note}</span> : null}
                    </div>
                    <div className="ea-activity__chips">
                      <span className={`ea-chip ea-chip--${meta.cls}`}>
                        {(m.type === "restock" || m.type === "adjust" ? "+" : "−")}
                        {n(m.quantity)} units ({n(m.stockBefore ?? m.stock_before)} →{" "}
                        {n(m.stockAfter ?? m.stock_after)})
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ea-activity__footer">
            <button type="button" className="ea-activity__clear" onClick={onRefresh}>
              🔄 Refresh log
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

const AddonCard = ({ item, onStock, onEdit, onDelete, busy }) => {
  const cat = getCategoryInfo(item.category);
  const isOut = item.stock === 0;
  const isLow = !isOut && item.stock <= item.minStock;
  const color = isOut ? "#dc2626" : isLow ? "#d97706" : "#16a34a";
  const status = isOut ? "Out of Stock" : isLow ? "Low Stock" : "In Stock";
  const barMax = Math.max(item.minStock * 3, item.stock, 1);

  return (
    <article className={`ea-card ${isOut ? "ea-card--out" : isLow ? "ea-card--low" : ""}`}>
      <div className="ea-card__header">
        <div className="ea-card__id">
          <span className="ea-card__emoji" style={{ background: `${cat.color}18`, color: cat.color }}>
            {item.emoji || cat.emoji}
          </span>
          <div className="ea-card__heading">
            <h3 className="ea-card__name" title={item.name}>{item.name}</h3>
            <span className="ea-card__cat" style={{ background: `${cat.color}15`, color: cat.color }}>
              {item.emoji || cat.emoji} {cat.label}
            </span>
          </div>
        </div>
        <span className={`ea-status ${isOut ? "ea-status--out" : isLow ? "ea-status--low" : "ea-status--ok"}`}>
          <span className="ea-status__dot" style={{ background: color }} />
          {status}
        </span>
      </div>

      <div className="ea-card__body">
        <div className="ea-card__row">
          <span className="ea-card__label">Price</span>
          <span className="ea-card__price">{money(item.price)}</span>
        </div>
        <div className="ea-card__stock-head">
          <span className="ea-card__label">Current Stock</span>
          <span className="ea-card__stock-num" style={{ color }}>{item.stock}</span>
        </div>
        <div className="ea-card__bar">
          <div
            className="ea-card__bar-fill"
            style={{ width: `${Math.min(100, (item.stock / barMax) * 100)}%`, background: color }}
          />
        </div>
        <div className="ea-card__meta">
          <span>Min: {item.minStock}</span>
          {item.sku ? <span title="SKU">{item.sku}</span> : <span>No SKU</span>}
        </div>
        {item.description ? <p className="ea-card__desc">{item.description}</p> : null}
        {(isOut || isLow) && (
          <div className={`ea-card__inline ${isOut ? "ea-card__inline--out" : "ea-card__inline--low"}`}>
            {isOut
              ? "🚫 Out of stock — restock immediately."
              : `⚠️ Only ${item.stock} unit${item.stock > 1 ? "s" : ""} left (min ${item.minStock}).`}
          </div>
        )}
      </div>

      <div className="ea-card__footer">
        <button className="ea-action ea-action--stock" disabled={busy} onClick={() => onStock(item)}>
          ➕ Add Stock
        </button>
        <button className="ea-action ea-action--edit" disabled={busy} onClick={() => onEdit(item)}>
          ✏️ Edit
        </button>
        <button className="ea-action ea-action--danger" disabled={busy} onClick={() => onDelete(item)}>
          🗑️ Remove
        </button>
      </div>
    </article>
  );
};

const EMPTY_FORM = {
  name: "",
  category: "gifts",
  sku: "",
  emoji: "🎁",
  price: 0,
  stock: 0,
  minStock: 5,
  description: "",
};

const ItemModal = ({ item, onSave, onClose, saving }) => {
  const isEditing = !!item;
  const [form, setForm] = useState(() =>
    item
      ? {
          name: item.name || "",
          category: item.category || "gifts",
          sku: item.sku || "",
          emoji: item.emoji || "🎁",
          price: item.price ?? 0,
          stock: item.stock ?? 0,
          minStock: item.minStock ?? 5,
          description: item.description || "",
        }
      : { ...EMPTY_FORM }
  );
  const [errors, setErrors] = useState({});

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!String(form.name).trim()) errs.name = "Item name is required.";
    if (n(form.price) < 0) errs.price = "Price cannot be negative.";
    if (n(form.stock) < 0) errs.stock = "Stock cannot be negative.";
    if (n(form.minStock) < 1) errs.minStock = "Minimum stock must be at least 1.";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const cat = getCategoryInfo(form.category);
    onSave({
      ...form,
      name: String(form.name).trim(),
      category: cat.key,
      sku: String(form.sku || "").trim().toUpperCase(),
      emoji: form.emoji || cat.emoji,
      price: Math.max(0, Math.round(n(form.price))),
      stock: Math.max(0, Math.floor(n(form.stock))),
      minStock: Math.max(1, Math.floor(n(form.minStock))),
      description: String(form.description || "").trim(),
    });
  };

  return (
    <div className="ea-overlay" onMouseDown={onClose}>
      <div className="ea-modal" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="ea-modal__header">
          <h2>{isEditing ? "✏️ Edit add-on" : "➕ Add new add-on"}</h2>
          <button type="button" className="ea-modal__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <form className="ea-modal__body" onSubmit={submit}>
          <label className="ea-field">
            <span>Item name *</span>
            <input
              autoFocus
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Crown Candles – 5Pcs"
              className={errors.name ? "invalid" : ""}
            />
            {errors.name ? <small className="ea-field__error">{errors.name}</small> : null}
          </label>

          <div className="ea-form-grid">
            <label className="ea-field">
              <span>Category</span>
              <select value={form.category} onChange={(e) => set("category", e.target.value)}>
                {CATEGORIES.filter((c) => c.key !== "all").map((c) => (
                  <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>
                ))}
              </select>
            </label>
            <label className="ea-field">
              <span>SKU code</span>
              <input
                type="text"
                value={form.sku}
                onChange={(e) => set("sku", e.target.value.toUpperCase())}
                placeholder="GFT-XXX-000"
              />
            </label>
          </div>

          <div className="ea-field">
            <span>Icon</span>
            <div className="ea-emoji-picker">
              {EMOJI_PRESETS.map((em) => (
                <button
                  type="button"
                  key={em}
                  className={`ea-emoji-opt ${form.emoji === em ? "selected" : ""}`}
                  onClick={() => set("emoji", em)}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>

          <div className="ea-form-grid ea-form-grid--3">
            <label className="ea-field">
              <span>Price (₹)</span>
              <input type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} />
            </label>
            <label className="ea-field">
              <span>Stock</span>
              <input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} />
            </label>
            <label className="ea-field">
              <span>Min. alert level</span>
              <input type="number" min="1" value={form.minStock} onChange={(e) => set("minStock", e.target.value)} />
            </label>
          </div>

          <label className="ea-field">
            <span>Description (optional)</span>
            <textarea
              rows="2"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Short description shown to customers…"
            />
          </label>

          <div className="ea-modal__footer">
            <button type="button" className="ea-btn ea-btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="ea-btn ea-btn--primary" disabled={saving}>
              {saving ? "Saving…" : isEditing ? "💾 Save changes" : "➕ Add item"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const StockModal = ({ item, onConfirm, onClose }) => {
  const [mode, setMode] = useState("add");
  const [amount, setAmount] = useState(1);
  const [note, setNote] = useState("");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const qty = Math.abs(Math.floor(n(amount)));
  const projected =
    mode === "set" ? Math.max(0, qty)
      : mode === "remove" ? Math.max(0, n(item.stock) - qty)
      : n(item.stock) + qty;

  const submit = (e) => {
    e.preventDefault();
    if (qty <= 0 && mode !== "set") return;
    onConfirm({ mode, amount: qty, note: note.trim(), projected });
  };

  return (
    <div className="ea-overlay" onMouseDown={onClose}>
      <div className="ea-modal ea-modal--sm" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="ea-modal__header">
          <h2>📦 Adjust stock</h2>
          <button type="button" className="ea-modal__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <form className="ea-modal__body" onSubmit={submit}>
          <div className="ea-stock-info">
            <span className="ea-stock-info__emoji">{item.emoji}</span>
            <div>
              <strong>{item.name}</strong>
              <div className="ea-stock-info__cur">
                Current stock on server: <b>{item.stock}</b>
              </div>
            </div>
          </div>

          <div className="ea-segmented">
            {[["add", "➕ Add"], ["remove", "➖ Remove"], ["set", "🎯 Set exact"]].map(([key, label]) => (
              <button
                type="button"
                key={key}
                className={`ea-segmented__btn ${mode === key ? "active" : ""}`}
                onClick={() => setMode(key)}
              >
                {label}
              </button>
            ))}
          </div>

          <label className="ea-field">
            <span>{mode === "set" ? "New stock level" : "Quantity"}</span>
            <input type="number" min="0" autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>

          <label className="ea-field">
            <span>Note (optional)</span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={mode === "add" ? "e.g. Supplier delivery" : "e.g. Damaged in transit"}
            />
          </label>

          <div className={`ea-projection ${projected < item.minStock ? "ea-projection--warn" : ""}`}>
            Projected stock after save: <b>{projected}</b>
            {projected === 0 ? <span className="ea-projection__tag">Out of stock</span> : null}
            {projected > 0 && projected <= item.minStock ? (
              <span className="ea-projection__tag">Below minimum</span>
            ) : null}
          </div>

          <div className="ea-modal__footer">
            <button type="button" className="ea-btn ea-btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="ea-btn ea-btn--primary">💾 Apply</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ConfirmModal = ({ title, message, confirmLabel, tone, onConfirm, onClose }) => (
  <div className="ea-overlay" onMouseDown={onClose}>
    <div className="ea-modal ea-modal--xs" onMouseDown={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
      <div className="ea-confirm">
        <div className={`ea-confirm__icon ea-confirm__icon--${tone || "danger"}`}>
          {tone === "warning" ? "⚠️" : "🗑️"}
        </div>
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="ea-confirm__actions">
          <button type="button" className="ea-btn ea-btn--ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className={`ea-btn ${tone === "warning" ? "ea-btn--warning" : "ea-btn--danger"}`}
            onClick={onConfirm}
          >
            {confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   5. MAIN MODULE
═══════════════════════════════════════════════════════════════════════════ */
const ExtraAddons = () => {
  /* Everything starts EMPTY on the server render — no fake rows. */
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [orderEvents, setOrderEvents] = useState([]);

  const [booted, setBooted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [online, setOnline] = useState(true);
  const [lastSync, setLastSync] = useState(0);
  const [cacheTime, setCacheTime] = useState(0);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState("cards");

  const [toasts, setToasts] = useState([]);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [stockTarget, setStockTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(false);

  const seenEventsRef = useRef(new Set());
  const syncInFlightRef = useRef(false);
  const channelRef = useRef(null);

  /* ── toasts ─────────────────────────────────────────────── */
  const notify = useCallback((message, type = "success") => {
    setToasts((prev) => [...prev.slice(-3), { message, type, id: `${Date.now()}_${Math.random()}` }]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /* ── low level API helper ───────────────────────────────── */
  const apiCall = useCallback(async (endpoint, options = {}) => {
    if (typeof window === "undefined") return { ok: false, status: 0 };
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        credentials: "include",
        cache: "no-store",
        headers: { "Content-Type": "application/json", ...(options.headers || {}) },
        ...options,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        return {
          ok: false,
          status: res.status,
          data: json,
          message: json?.message || json?.error || `Request failed (${res.status})`,
        };
      }
      return { ok: true, status: res.status, data: json };
    } catch (error) {
      return { ok: false, status: 0, error, message: error?.message || "Network error" };
    }
  }, []);

  /* ── single source of truth: the server ─────────────────── */
  const sync = useCallback(
    async ({ silent = true } = {}) => {
      if (syncInFlightRef.current) return;
      syncInFlightRef.current = true;
      if (!silent) setRefreshing(true);

      const res = await apiCall("/all");
      if (res.ok && res.data) {
        const rows = (Array.isArray(res.data.data) ? res.data.data : Array.isArray(res.data) ? res.data : [])
          .map(normalizeItem)
          .filter(Boolean);
        setItems(rows);
        writeCache(rows);
        setCacheTime(Date.now());
        setOnline(true);
        setLastSync(Date.now());
      } else if (res.status === 0) {
        setOnline(false);
      } else {
        setOnline(true);
        if (!silent) notify(res.message || "Server rejected the request.", "error");
      }

      const [mv, ev] = await Promise.all([apiCall("/movements"), apiCall("/events")]);
      if (mv.ok) {
        const rows = Array.isArray(mv.data?.data) ? mv.data.data : [];
        setMovements(rows.slice(0, 40));
      }
      if (ev.ok) {
        const rows = Array.isArray(ev.data?.data) ? ev.data.data : [];
        rows.forEach((r) => seenEventsRef.current.add(String(r.id || r._id || "")));
        setOrderEvents(rows.slice(0, 40));
      }

      if (!silent) setRefreshing(false);
      syncInFlightRef.current = false;
    },
    [apiCall, notify]
  );

  /* ── boot: paint cached server snapshot, then go live ───── */
  useEffect(() => {
    const cached = readCache();
    if (cached.items.length) {
      setItems(cached.items);
      setCacheTime(cached.savedAt);
    }
    setBooted(true);
    sync({ silent: true }).finally(() => setLoading(false));

    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(SYNC_CHANNEL);
      channel.onmessage = (e) => {
        const type = e?.data?.type;
        if (type === "order-event") {
          // Another tab of THIS browser got a frozen order — pull server truth.
          seenEventsRef.current.add(String(e.data.eventId || ""));
          sync({ silent: true });
          notify(
            `Stock reduced — ${e.data.orderName || "a frozen order"} was confirmed in another tab.`,
            "warning"
          );
        } else if (type === "items-changed") {
          sync({ silent: true });
        }
      };
      channelRef.current = channel;
    }

    const onStorage = (e) => {
      if (e.key === CACHE_KEY) {
        const cached2 = readCache();
        if (cached2.items.length) {
          setItems((prev) =>
            prev.length === 0 && cached2.items.length ? cached2.items : prev
          );
        }
      }
    };
    window.addEventListener("storage", onStorage);

    const poll = setInterval(() => sync({ silent: true }), POLL_MS);
    const onFocus = () => {
      if (document.visibilityState === "visible") sync({ silent: true });
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onFocus);

    return () => {
      clearInterval(poll);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onFocus);
      if (channelRef.current) channelRef.current.close();
    };
  }, [sync, notify]);

  /* ── broadcast helpers (same browser, other tabs) ───────── */
  const broadcast = useCallback((type, extra = {}) => {
    try {
      if (channelRef.current) channelRef.current.postMessage({ type, ...extra });
    } catch (e) {
      /* ignore */
    }
  }, []);

  /* ── frozen-order deduction listener ────────────────────── */
  useEffect(() => {
    const handleOrder = async (detail) => {
      const event = detail?.event || detail?.stockEvent || detail;
      const rawItems = Array.isArray(event?.items) ? event.items : Array.isArray(event?.deductedItems) ? event.deductedItems : [];
      const payloadItems = buildDeductPayload(rawItems);
      if (!payloadItems.length) return;

      const res = await apiCall("/deduct-order", {
        method: "POST",
        body: JSON.stringify({
          orderId: event?.orderId || event?.orderNumber || "",
          orderName: event?.orderName || event?.customerName || "",
          theater: event?.theater || "",
          eventDate: event?.eventDate || "",
          items: payloadItems,
        }),
      });

      if (res.ok) {
        const applied = res.data?.data || {};
        if (Array.isArray(applied.items)) {
          setItems(applied.items.map(normalizeItem).filter(Boolean));
          writeCache(applied.items);
        }
        if (Array.isArray(applied.event)) {
          /* noop — events are re-pulled by sync */
        }
        seenEventsRef.current.add(String(applied.eventId || event?.orderId || ""));
        broadcast("order-event", {
          eventId: String(applied.eventId || event?.orderId || ""),
          orderName: event?.orderName || "",
        });
        notify(
          `Stock auto-reduced — ${event?.orderName || "a frozen order"} confirmed, ${payloadItems.length} item(s) updated.`,
          "success"
        );
        sync({ silent: true });
      } else if (res.status === 0) {
        notify("Order received but the server is unreachable — will retry on reconnect.", "error");
      } else {
        notify(res.message || "Could not apply the stock deduction.", "error");
      }
    };

    const onCustom = (e) => {
      const detail = e?.detail;
      if (detail) handleOrder(detail);
      else sync({ silent: true });
    };

    window.addEventListener("extraAddonUpdated", onCustom);
    window.addEventListener("stockUpdated", onCustom);
    window.addEventListener("hexaplay:order-confirmed", onCustom);

    return () => {
      window.removeEventListener("extraAddonUpdated", onCustom);
      window.removeEventListener("stockUpdated", onCustom);
      window.removeEventListener("hexaplay:order-confirmed", onCustom);
    };
  }, [apiCall, broadcast, notify, sync]);

  /* ── derived ────────────────────────────────────────────── */
  const stats = useMemo(() => {
    const totalItems = items.length;
    const totalValue = items.reduce((a, i) => a + n(i.price) * n(i.stock), 0);
    const units = items.reduce((a, i) => a + n(i.stock), 0);
    const low = items.filter((i) => i.stock > 0 && i.stock <= i.minStock).length;
    const out = items.filter((i) => i.stock === 0).length;
    return { totalItems, totalValue, units, low, out };
  }, [items]);

  const categoryCounts = useMemo(() => {
    const map = { all: { count: items.length, low: 0, out: 0 } };
    CATEGORIES.filter((c) => c.key !== "all").forEach((c) => {
      map[c.key] = { count: 0, low: 0, out: 0 };
    });
    items.forEach((i) => {
      if (!map[i.category]) map[i.category] = { count: 0, low: 0, out: 0 };
      map[i.category].count += 1;
      if (i.stock === 0) {
        map[i.category].out += 1;
        map.all.out += 1;
      } else if (i.stock <= i.minStock) {
        map[i.category].low += 1;
        map.all.low += 1;
      }
    });
    return map;
  }, [items]);

  const filtered = useMemo(() => {
    let list = [...items];
    if (category !== "all") list = list.filter((i) => i.category === category);
    if (stockFilter === "low") list = list.filter((i) => i.stock > 0 && i.stock <= i.minStock);
    if (stockFilter === "out") list = list.filter((i) => i.stock === 0);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          i.category.toLowerCase().includes(q) ||
          (i.sku || "").toLowerCase().includes(q) ||
          (i.description || "").toLowerCase().includes(q)
      );
    }

    const created = (x) => new Date(x.createdAt || 0).getTime() || 0;
    const sorters = {
      "name-asc": (a, b) => a.name.localeCompare(b.name),
      "name-desc": (a, b) => b.name.localeCompare(a.name),
      "price-desc": (a, b) => b.price - a.price,
      "price-asc": (a, b) => a.price - b.price,
      "stock-asc": (a, b) => a.stock - b.stock,
      "stock-desc": (a, b) => b.stock - a.stock,
      oldest: (a, b) => created(a) - created(b),
      newest: (a, b) => created(b) - created(a),
    };
    return list.sort(sorters[sortBy] || sorters.newest);
  }, [items, category, stockFilter, search, sortBy]);

  /* ── CRUD ───────────────────────────────────────────────── */
  const openAdd = () => {
    setEditingItem(null);
    setItemModalOpen(true);
  };
  const openEdit = (item) => {
    setEditingItem(item);
    setItemModalOpen(true);
  };

  const handleSave = async (data) => {
    setSaving(true);
    const isEditing = !!editingItem;
    const target = editingItem;

    if (isEditing) {
      setItems((prev) =>
        prev.map((i) => (i.id === target.id ? normalizeItem({ ...i, ...data, id: target.id }) : i))
      );
      const res = await apiCall(`/${data.category}/${target.id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
      if (res.ok) {
        notify(`"${data.name}" updated.`, "success");
        broadcast("items-changed");
      } else if (res.status === 0) {
        notify("Server unreachable — change not saved. Restore connection and retry.", "error");
        setItems((prev) => prev.map((i) => (i.id === target.id ? target : i)));
      } else {
        notify(res.message || "Update failed.", "error");
        setItems((prev) => prev.map((i) => (i.id === target.id ? target : i)));
      }
    } else {
      const tempId = `pending_${Date.now()}`;
      setItems((prev) => [normalizeItem({ ...data, id: tempId }), ...prev.filter(Boolean)]);
      const res = await apiCall(`/${data.category}`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (res.ok) {
        notify(`"${data.name}" added to inventory.`, "success");
        broadcast("items-changed");
      } else if (res.status === 0) {
        notify("Server unreachable — item not saved. Restore connection and retry.", "error");
        setItems((prev) => prev.filter((i) => i.id !== tempId));
      } else {
        notify(res.message || "Could not create the item.", "error");
        setItems((prev) => prev.filter((i) => i.id !== tempId));
      }
    }

    setItemModalOpen(false);
    setEditingItem(null);
    setSaving(false);
    await sync({ silent: true });
  };

  const handleStockConfirm = async ({ mode, amount, note, projected }) => {
    const target = stockTarget;
    setStockTarget(null);
    if (!target) return;

    const before = target.stock;
    setItems((prev) => prev.map((i) => (i.id === target.id ? { ...i, stock: projected } : i)));

    const res = await apiCall(`/${target.category}/${target.id}/stock`, {
      method: "PATCH",
      body: JSON.stringify({
        mode,
        delta: mode === "set" ? projected : amount,
        newStock: mode === "set" ? projected : undefined,
        note: note || (mode === "add" ? "Manual restock" : "Manual adjustment"),
        type: mode === "add" ? "restock" : mode === "remove" ? "deduct" : "adjust",
      }),
    });

    if (res.ok) {
      const label =
        mode === "set" ? `Stock set to ${projected}`
          : mode === "remove" ? `Removed ${amount} unit${amount > 1 ? "s" : ""}`
          : `Added ${amount} unit${amount > 1 ? "s" : ""}`;
      notify(`${label} for "${target.name}" — saved for every device.`, "success");
      broadcast("items-changed");
    } else if (res.status === 0) {
      notify("Server unreachable — stock not changed.", "error");
      setItems((prev) => prev.map((i) => (i.id === target.id ? { ...i, stock: before } : i)));
    } else {
      notify(res.message || "Stock update failed.", "error");
      setItems((prev) => prev.map((i) => (i.id === target.id ? { ...i, stock: before } : i)));
    }
    await sync({ silent: true });
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;

    const snapshot = items;
    setItems((prev) => prev.filter((i) => i.id !== target.id));
    const res = await apiCall(`/${target.category}/${target.id}`, { method: "DELETE" });
    if (res.ok) {
      notify(`"${target.name}" removed from inventory.`, "success");
      broadcast("items-changed");
    } else if (res.status === 0) {
      notify("Server unreachable — item still exists on other devices.", "error");
      setItems(snapshot);
    } else {
      notify(res.message || "Delete failed.", "error");
      setItems(snapshot);
    }
    await sync({ silent: true });
  };

  /* ── import / export ────────────────────────────────────── */
  const handleExport = () => {
    const payload = {
      exportedAt: new Date().toISOString(),
      source: "The Hexaplay — Extra Add-ons",
      count: items.length,
      items: items.map((i) => ({
        name: i.name,
        category: i.category,
        sku: i.sku,
        emoji: i.emoji,
        price: i.price,
        stock: i.stock,
        minStock: i.minStock,
        description: i.description,
        isActive: i.isActive,
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `extra-addons-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    notify("Inventory exported as JSON.", "success");
  };

  const handleImport = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json,application/json";
    input.onchange = (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const parsed = JSON.parse(String(ev.target?.result || ""));
          const list = Array.isArray(parsed) ? parsed : parsed.items;
          if (!Array.isArray(list) || !list.length) throw new Error("No items found in file");
          const res = await apiCall("/bulk", {
            method: "POST",
            body: JSON.stringify({ items: list.map((x) => ({ ...x })) }),
          });
          if (res.ok) {
            notify(
              `Imported ${res.data?.imported ?? list.length} item(s)${
                res.data?.skipped ? `, skipped ${res.data.skipped}` : ""
              }.`,
              "success"
            );
            broadcast("items-changed");
            await sync({ silent: true });
          } else {
            notify(res.message || "Import rejected by the server.", "error");
          }
        } catch (err) {
          notify(`Invalid import file: ${err.message}`, "error");
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleResetInventory = async () => {
    setResetTarget(false);
    const ids = items.map((i) => ({ id: i.id, category: i.category }));
    setItems([]);
    let failed = 0;
    for (const row of ids) {
      const res = await apiCall(`/${row.category}/${row.id}`, { method: "DELETE" });
      if (!res.ok) failed += 1;
    }
    if (failed) notify(`${failed} item(s) could not be deleted — reconnect and retry.`, "error");
    else notify("Inventory cleared on the server.", "success");
    writeCache([]);
    broadcast("items-changed");
    await sync({ silent: true });
  };

  const busyRow = (id) => !online && String(id).startsWith("pending_");

  /* ── render ─────────────────────────────────────────────── */
  return (
    <div className="ea-shell">
      {/* toasts */}
      <div className="ea-toasts">
        {toasts.map((t) => (
          <Toast key={t.id} toast={t} onClose={() => dismissToast(t.id)} />
        ))}
      </div>

      {itemModalOpen && (
        <ItemModal
          item={editingItem}
          saving={saving}
          onSave={handleSave}
          onClose={() => {
            setItemModalOpen(false);
            setEditingItem(null);
          }}
        />
      )}

      {stockTarget && (
        <StockModal item={stockTarget} onConfirm={handleStockConfirm} onClose={() => setStockTarget(null)} />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Remove add-on?"
          message={`"${deleteTarget.name}" will be deleted from the database and disappear on every device. This cannot be undone.`}
          confirmLabel="🗑️ Yes, remove"
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {resetTarget && (
        <ConfirmModal
          title="Clear the whole inventory?"
          message="Every add-on currently stored in the database will be deleted for all devices and browsers. Export a backup first if you need one."
          confirmLabel="🗑️ Delete everything"
          tone="warning"
          onConfirm={handleResetInventory}
          onClose={() => setResetTarget(false)}
        />
      )}

      <main className="ea-page">
        {/* header */}
        <div className="ea-header">
          <div className="ea-header__left">
            <span className="ea-header__icon">📦</span>
            <div>
              <h1>
                Extra Add-ons
                <span className={`ea-conn ${online ? "ea-conn--on" : "ea-conn--off"}`}>
                  <span className="ea-conn__dot" />
                  {online ? "Live sync" : "Server offline"}
                </span>
              </h1>
              <p>
                Real inventory from the database — cakes, decorations, gifts &amp; services.
                {online && lastSync ? (
                  <span className="ea-header__sync"> Synced {relativeTime(lastSync)}.</span>
                ) : null}
                {!online && cacheTime ? (
                  <span className="ea-header__sync"> Showing saved copy from {relativeTime(cacheTime)}.</span>
                ) : null}
              </p>
            </div>
          </div>
          <div className="ea-header__right">
            <button
              type="button"
              className="ea-btn ea-btn--outline"
              onClick={() => sync({ silent: false })}
              disabled={refreshing}
            >
              {refreshing ? "⟳ Syncing…" : "🔄 Refresh"}
            </button>
            <button type="button" className="ea-btn ea-btn--outline" onClick={handleImport}>📥 Import</button>
            <button type="button" className="ea-btn ea-btn--outline" onClick={handleExport}>📤 Export</button>
            <button type="button" className="ea-btn ea-btn--primary" onClick={openAdd}>➕ Add Item</button>
          </div>
        </div>

        {!online && (
          <div className="ea-offline">
            ⚠️ Cannot reach <code>{API_URL}</code>. Nothing can be added or edited right now —
            this screen never invents sample data, so only your last saved server copy is shown.
          </div>
        )}

        {loading ? (
          <div className="ea-loading">
            <span className="ea-spinner" />
            <p>Loading live inventory…</p>
          </div>
        ) : (
          <>
            <AlertBanner items={items} onFilter={setStockFilter} />

            <ActivityLog
              orderEvents={orderEvents}
              movements={movements}
              onRefresh={() => sync({ silent: true })}
            />

            {/* stats */}
            <div className="ea-stats">
              <div className="ea-stat">
                <span className="ea-stat__icon ea-stat__icon--indigo">📦</span>
                <div>
                  <div className="ea-stat__value">{stats.totalItems}</div>
                  <div className="ea-stat__label">Total Items</div>
                </div>
              </div>
              <div className="ea-stat">
                <span className="ea-stat__icon ea-stat__icon--green">💰</span>
                <div>
                  <div className="ea-stat__value">{money(stats.totalValue)}</div>
                  <div className="ea-stat__label">Total Stock Value</div>
                </div>
              </div>
              <div className="ea-stat">
                <span className="ea-stat__icon ea-stat__icon--cyan">🧮</span>
                <div>
                  <div className="ea-stat__value">{stats.units}</div>
                  <div className="ea-stat__label">Units In Hand</div>
                </div>
              </div>
              <button
                type="button"
                className={`ea-stat ea-stat--btn ${stats.low ? "ea-stat--pulse-amber" : ""}`}
                onClick={() => stats.low && setStockFilter("low")}
              >
                <span className="ea-stat__icon ea-stat__icon--amber">⚠️</span>
                <div>
                  <div className="ea-stat__value">{stats.low}</div>
                  <div className="ea-stat__label">Low Stock Alerts</div>
                </div>
                {stats.low > 0 ? <span className="ea-stat__pill">Click to view</span> : null}
              </button>
              <button
                type="button"
                className={`ea-stat ea-stat--btn ${stats.out ? "ea-stat--pulse-red" : ""}`}
                onClick={() => stats.out && setStockFilter("out")}
              >
                <span className="ea-stat__icon ea-stat__icon--red">🚫</span>
                <div>
                  <div className="ea-stat__value">{stats.out}</div>
                  <div className="ea-stat__label">Out of Stock</div>
                </div>
                {stats.out > 0 ? <span className="ea-stat__pill ea-stat__pill--red">Click to view</span> : null}
              </button>
            </div>

            {/* toolbar */}
            <div className="ea-toolbar">
              <div className="ea-toolbar__left">
                <div className="ea-search">
                  <span className="ea-search__icon">🔍</span>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, category or SKU…"
                  />
                  {search ? (
                    <button
                      type="button"
                      className="ea-search__clear"
                      onClick={() => setSearch("")}
                      aria-label="Clear search"
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
                <div className="ea-pills">
                  {[["all", "All"], ["low", "⚠️ Low"], ["out", "🚫 Out"]].map(([key, label]) => (
                    <button
                      type="button"
                      key={key}
                      className={`ea-pills__btn ${stockFilter === key ? "active" : ""}`}
                      onClick={() => setStockFilter(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ea-toolbar__right">
                <select
                  className="ea-sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort items"
                >
                  {SORTS.map((s) => (
                    <option key={s.key} value={s.key}>Sort: {s.label}</option>
                  ))}
                </select>
                <div className="ea-view">
                  <button
                    type="button"
                    className={`ea-view__btn ${viewMode === "cards" ? "active" : ""}`}
                    onClick={() => setViewMode("cards")}
                    title="Card view"
                    aria-label="Card view"
                  >
                    ▦
                  </button>
                  <button
                    type="button"
                    className={`ea-view__btn ${viewMode === "table" ? "active" : ""}`}
                    onClick={() => setViewMode("table")}
                    title="Table view"
                    aria-label="Table view"
                  >
                    ☰
                  </button>
                </div>
              </div>
            </div>

            {/* category tabs */}
            <div className="ea-tabs">
              {CATEGORIES.map((cat) => {
                const c = categoryCounts[cat.key] || { count: 0, low: 0, out: 0 };
                return (
                  <button
                    type="button"
                    key={cat.key}
                    className={`ea-tab ${category === cat.key ? "active" : ""}`}
                    onClick={() => setCategory(cat.key)}
                    style={{ "--ea-cat": cat.color }}
                  >
                    <span>{cat.emoji}</span>
                    <span className="ea-tab__label">{cat.label}</span>
                    <span className="ea-tab__count">{c.count}</span>
                    {c.out > 0 ? (
                      <span className="ea-tab__badge ea-tab__badge--out">{c.out}</span>
                    ) : c.low > 0 ? (
                      <span className="ea-tab__badge ea-tab__badge--low">{c.low}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div className="ea-results">
              <span>
                Showing <b>{filtered.length}</b> of <b>{items.length}</b> database item{items.length === 1 ? "" : "s"}
              </span>
              {stockFilter !== "all" ? (
                <button type="button" className="ea-results__clear" onClick={() => setStockFilter("all")}>
                  ✕ Clear stock filter
                </button>
              ) : null}
              {category !== "all" ? (
                <button type="button" className="ea-results__clear" onClick={() => setCategory("all")}>
                  ✕ Clear category
                </button>
              ) : null}
            </div>

            {/* list */}
            {filtered.length === 0 ? (
              <div className="ea-empty">
                <div className="ea-empty__icon">📭</div>
                <h3>{items.length === 0 ? "No add-ons in the database yet" : "No add-ons match your filters"}</h3>
                <p>
                  {items.length === 0
                    ? "This page only shows real inventory saved on the server — there is no demo data. Create your first real add-on to get started."
                    : search
                    ? `Nothing matches “${search}”.`
                    : stockFilter !== "all"
                    ? "No items match this stock filter."
                    : "No items in this category yet."}
                </p>
                {items.length === 0 ? (
                  <button type="button" className="ea-btn ea-btn--primary" onClick={openAdd}>
                    ➕ Create first add-on
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ea-btn ea-btn--outline"
                    onClick={() => {
                      setSearch("");
                      setStockFilter("all");
                      setCategory("all");
                    }}
                  >
                    ↺ Reset filters
                  </button>
                )}
              </div>
            ) : viewMode === "cards" ? (
              <div className="ea-grid">
                {filtered.map((item) => (
                  <AddonCard
                    key={item.id}
                    item={item}
                    busy={busyRow(item.id)}
                    onStock={setStockTarget}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </div>
            ) : (
              <div className="ea-table-wrap">
                <table className="ea-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item</th>
                      <th>SKU</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Stock</th>
                      <th>Value</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((item, idx) => {
                      const cat = getCategoryInfo(item.category);
                      const isOut = item.stock === 0;
                      const isLow = !isOut && item.stock <= item.minStock;
                      const color = isOut ? "#dc2626" : isLow ? "#d97706" : "#16a34a";
                      const status = isOut ? "Out of Stock" : isLow ? "Low Stock" : "In Stock";
                      return (
                        <tr key={item.id} className={isOut ? "ea-tr--out" : isLow ? "ea-tr--low" : ""}>
                          <td className="ea-table__idx">{idx + 1}</td>
                          <td>
                            <div className="ea-table__name">
                              <span>{item.emoji || cat.emoji}</span>
                              <b>{item.name}</b>
                            </div>
                          </td>
                          <td className="ea-table__sku">{item.sku || "—"}</td>
                          <td>
                            <span className="ea-table__cat" style={{ background: `${cat.color}18`, color: cat.color }}>
                              {cat.label}
                            </span>
                          </td>
                          <td className="ea-table__price">{money(item.price)}</td>
                          <td><b style={{ color }}>{item.stock}</b></td>
                          <td className="ea-table__price">{money(n(item.price) * n(item.stock))}</td>
                          <td>
                            <span
                              className="ea-table__status"
                              style={{ background: `${color}15`, color, border: `1px solid ${color}33` }}
                            >
                              {status}
                            </span>
                          </td>
                          <td>
                            <div className="ea-table__actions">
                              <button type="button" onClick={() => setStockTarget(item)} title="Adjust stock">➕</button>
                              <button type="button" onClick={() => openEdit(item)} title="Edit">✏️</button>
                              <button type="button" onClick={() => setDeleteTarget(item)} title="Remove">🗑️</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {items.length > 0 && (
              <div className="ea-dangerbar">
                <span>Inventory is stored in the database and shared by every browser.</span>
                <button type="button" className="ea-btn ea-btn--danger-ghost" onClick={() => setResetTarget(true)}>
                  🧨 Clear entire inventory
                </button>
              </div>
            )}

            <footer className="ea-footer">
              💡 Confirming a frozen order instantly reduces <b>Current Stock</b> here and on every
              other Chrome profile, browser and device — the database is the single source of truth.
            </footer>
          </>
        )}
      </main>
    </div>
  );
};

export default ExtraAddons;
